import React, { useEffect, useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';

const ALL_STATUSES = ['Available', 'AdoptionPending', 'Adopted', 'Foster', 'Medical', 'StrayHold', 'Quarantine'];

const STATUS_LABELS: Record<string, string> = {
  Available: 'Gazdikereső', AdoptionPending: 'Örökbefogadás folyamatban', Adopted: 'Örökbefogadva',
  Foster: 'Ideiglenesben', Medical: 'Orvosi kezelés alatt', StrayHold: 'Várakozási idő (Stray)', Quarantine: 'Karantén',
};

interface StatusChangeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  animalName: string;
  currentStatus: string;
  onConfirm: (newStatus: string, reason: string) => Promise<void>;
}

/**
 * Manuális státuszváltó dialog — bármely státuszból bármely másikba
 * átállítható (pl. StrayHold → Available, ha lejárt a törvényes várakozási
 * idő, amit jelenleg semmilyen automatizmus nem figyel). Minden váltás
 * naplózva van szerver oldalon (animal_status_changes — l. 030_animal_status_log.sql).
 */
export const StatusChangeDialog: React.FC<StatusChangeDialogProps> = ({
  isOpen, onClose, animalName, currentStatus, onConfirm,
}) => {
  const [selected, setSelected] = useState(currentStatus);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(currentStatus);
      setReason('');
    }
  }, [isOpen, currentStatus]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setSaving(true);
    await onConfirm(selected, reason.trim());
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Státusz módosítása</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-500">
            {animalName} jelenlegi státusza: <span className="font-semibold text-gray-700">{STATUS_LABELS[currentStatus] || currentStatus}</span>
          </p>

          <div className="grid grid-cols-1 gap-2">
            {ALL_STATUSES.map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setSelected(s)}
                className={`text-left px-4 py-2.5 rounded-lg border transition-colors ${
                  selected === s ? 'border-teal-500 bg-teal-50 text-teal-800 font-semibold' : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                }`}
              >
                {STATUS_LABELS[s] || s}
                {s === currentStatus && <span className="ml-2 text-xs text-gray-400 font-normal">(jelenlegi)</span>}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 opacity-[0.85]">Indoklás</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
              placeholder="Pl. lejárt a törvényes várakozási idő"
            />
            <p className="text-xs text-gray-400 mt-1.5">A módosítás (ki, mikor, miről mire) naplózásra kerül.</p>
          </div>
        </div>

        <div className="p-6 pt-0 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors">
            Mégsem
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || selected === currentStatus}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Mentés
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusChangeDialog;
